import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import type {Tag} from '@/hooks/empresa/organizacion/use-tags'
import {normalize} from '@/lib/utils'

/** Submits names: the backend find-or-creates on save, so a reused tag stays the same row. */
export function TagCombobox({
  label,
  testId,
  available,
  value,
  onChange,
  onQueryChange,
}: {
  label: string
  testId: string
  available: Tag[]
  value: string[]
  onChange: (names: string[]) => void
  /** Para buscar en el servidor: el filtro local solo ve la página que llegó (R6/R3). */
  onQueryChange?: (query: string) => void
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')

  const selectedKeys = useMemo(() => new Set(value.map(normalize)), [value])
  const normalizedQuery = normalize(query)

  const matches = useMemo(() => {
    if (!normalizedQuery) return []
    return available
      .filter((tag) => !selectedKeys.has(normalize(tag.name)) && normalize(tag.name).includes(normalizedQuery))
      .slice(0, 6)
  }, [available, normalizedQuery, selectedKeys])

  const exactExists = useMemo(
    () => available.some((tag) => normalize(tag.name) === normalizedQuery),
    [available, normalizedQuery]
  )
  const canCreate = !!normalizedQuery && !selectedKeys.has(normalizedQuery) && !exactExists

  function add(name: string) {
    const trimmed = name.trim()
    if (trimmed && !selectedKeys.has(normalize(trimmed))) onChange([...value, trimmed])
    setQuery('')
  }

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((name) => (
            <button
              key={name}
              type="button"
              data-testid={`${testId}-chip`}
              onClick={() => onChange(value.filter((n) => n !== name))}
              className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground hover:opacity-75">
              <span className="min-w-0 truncate">{name}</span>
              <span aria-hidden="true" className="shrink-0 font-normal opacity-60">
                ×
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          data-testid={`${testId}-input`}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            onQueryChange?.(e.target.value)
          }}
          placeholder={t('tags.combobox.placeholder')}
          className="h-11 w-full rounded-lg border border-input bg-transparent px-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:h-9 md:text-sm"
        />
        {(matches.length > 0 || canCreate) && (
          <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-border bg-popover shadow-[var(--shadow-pop)]">
            {matches.map((tag) => (
              <li key={tag.id}>
                <button
                  type="button"
                  data-testid={`${testId}-option`}
                  onClick={() => add(tag.name)}
                  className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm hover:bg-secondary md:py-2">
                  <span className="min-w-0 truncate">{tag.name}</span>
                  <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">
                    {t('tags.usage', {count: tag.usage_count})}
                  </span>
                </button>
              </li>
            ))}
            {canCreate && (
              <li className={matches.length > 0 ? 'border-t border-border' : undefined}>
                <button
                  type="button"
                  data-testid={`${testId}-create`}
                  onClick={() => add(query)}
                  className="flex w-full items-center px-3 py-3 text-left text-sm text-primary hover:bg-secondary md:py-2">
                  <span className="min-w-0 truncate">{t('tags.combobox.create', {name: query.trim()})}</span>
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}
